<template>
	<div class="all-payroll-groups">
		<v-row class="mb-2 mt-1">
			<v-col cols="12" md="8">
				<h1 class="text-left mx-4 mt-2">Payroll Groups</h1>
			</v-col>
			<v-col cols="12" md="4" v-if="currentUser.privileges.canAddPayrollGroups">
				<v-btn
					:to="{ name: 'AddPayrollGroup' }"
					color="primary"
					class="mx-4 mt-2"
					id="add-payroll-groups"
					>Add Payroll Group</v-btn
				>
			</v-col>
		</v-row>

		<v-row>
			<v-tabs>
				<v-tab class="ml-4">Groups</v-tab>
				<v-tab v-if="currentUser.privileges.canAllocatePayrollGroups">Allocate</v-tab>
				<!-- Category tab item -->
				<v-tab-item class="mx-4">
					<v-row>
						<v-col cols="12" class="py-0">
							<h2 class="my-4" v-if="!payrollGroups.length && !isLoading">
								No Groups Found.
							</h2>
						</v-col>
						<v-col
							cols="12"
							md="4"
							v-for="group in payrollGroups"
							:key="group.id"
						>
							<v-card
								class="py-4 mx-4 my-4 px-4 group-card"
								:to="{ name: 'EditPayrollGroup', params: { id: group.id } }"
							>
								<!-- Add edit category id in the v-card above -->
								<h2 class="text-left font-weight-medium mb-4">
									{{ group.title }}
								</h2>
								<p class="text--secondary text-left font-weight-medium mb-0">
									<small>{{ group.code }}</small>
								</p>
							</v-card>
						</v-col>
					</v-row>
				</v-tab-item>

				<v-tab-item class="mx-8 my-4" v-if="currentUser.privileges.canAllocatePayrollGroups">
					<v-card>
						<v-card-title>
							Payroll Details
							<v-spacer></v-spacer>
							<v-text-field
								v-model="search"
								append-icon="mdi-magnify"
								label="Search"
								single-line
								hide-details
							></v-text-field>
						</v-card-title>

						<v-data-table
							:headers="headers"
							:items="accounts"
							:search="search"
							:options.sync="options"
							:multi-sort="true"
							:items-per-page="15"
							:server-items-length="pagination.totalCount"
							class="row-pointer"
						>
							<template v-slot:item.staff.employee_id="{ item }">
								<h5>
									{{
										item.staff.employee_id != null ? item.staff.employee_id : "-"
									}}
								</h5>
							</template>
							<template v-slot:item.staff.image="{ item }">
								<v-avatar width="30" height="30" class="mb-2">
									<img :src="item.staff.image" />
								</v-avatar>
							</template>
							<template v-slot:item.staff.full_name="{ item }">
								<v-btn
									color="primary"
									link
									text
									:ripple="false"
									@click="redirectToDetails(item.id)"
								>
									<h5>{{ item.staff.full_name }}</h5>
								</v-btn>
							</template>

							<template v-slot:item.payroll_group.code="{ item }">
								<v-row justify="end">
									<v-select
										:items="payrollGroups"
										v-model="item.payroll_group"
										chips
										flat
										tile
										solo
										hide-details
										:suffix="item.payroll_group && item.payroll_group.code ? null : 'Select Group'"
										class="selections"
										background-color="transparent"
										return-object
										@change="savePayrollGroup(item)"
										item-text="code"
									>
										<template v-slot:selection="{ item, select }">
											<v-chip color="primary" outlined @click="select">
												<strong>{{ item.code }}</strong>
											</v-chip>
										</template>
									</v-select>
								</v-row>
							</template>
						</v-data-table>
					</v-card>
				</v-tab-item>
			</v-tabs>
		</v-row>

		<v-row justify="center">
			<ConfirmationDialog
				title="Are you sure you want to change this staff's payroll group?"
				description="Please enter values for the selected group's particulars below."
				successText="Proceed"
				@successCallback="groupChanged()"
				:disableSuccessButton="!payrollDetailsForm"
				failureText="Cancel"
				@failureCallback="closeDialog"
				:visible="dialog"
			>
				<div>
					<v-form v-model="payrollDetailsForm">
					<h4 class="text-left mx-4 mb-2">Earnings</h4>
						<div
							v-for="data in selectedGroupDetails.particulars"
							:key="data.id"
						>
							<div v-if="data.particular_type == 'E'">
								<v-text-field
									outlined
									dense
									:label="data.title"
									v-model="data.value"
									class="mx-4 rounded-input"
									prepend-icon="mdi-currency-inr"
									:rules="[rules.required]"
									type="number"
								>
								</v-text-field>
							</div>
						</div>
						<h5 class="mx-4 mb-2 green--text text-left">Total Fixed Earnings : {{ Helper.currencyFormat(getDialogTotalEarnings()) }} </h5>

						<div v-if="selectedGroupDetails.account_OT">
							<v-text-field
								outlined
								dense
								label="Overtime Hourly Rate"
								v-model="desiredGroup.ot_rate"
								:rules="[rules.required]"
								prepend-icon="mdi-currency-inr"
								class="mx-4 rounded-input"
								type="number"
							>
							</v-text-field>
						</div>
						<v-divider class="mx-2 mb-4"></v-divider>
						<h4 class="text-left mx-4 mb-2">Deductions</h4>
						<div
							v-for="data in selectedGroupDetails.particulars"
							:key="data.code"
						>
							<div v-if="data.particular_type == 'D'">
								<v-text-field
									outlined
									dense
									:label="data.title"
									prepend-icon="mdi-currency-inr"
									:rules="[rules.required]"
									v-model="data.value"
									class="mx-4 rounded-input"
									type="number"

								>
								</v-text-field>
							</div>
						</div>
						<h5 class="mx-4 mb-2 red--text text-left">Total Fixed Deductions : {{ Helper.currencyFormat(getDialogTotalDeductions()) }} </h5>
						<div v-if="selectedGroupDetails.account_leaves">
							<v-text-field
								outlined
								dense
								label="Loss of Pay for Leaves (Per Day)"
								v-model="desiredGroup.loss_of_pay"
								:rules="[rules.required]"
								prepend-icon="mdi-currency-inr"
								class="mx-4 rounded-input"
								type="number"
							>
							</v-text-field>
						</div>
						<v-divider class="mx-2 mb-4"></v-divider>
						<h5 class="mx-4 mb-2 green--text text-left">Fixed Net Salary : {{ Helper.currencyFormat(getDialogNetSalary()) }} </h5>
						</v-form>
					</div>
			</ConfirmationDialog>
		</v-row>

		<v-snackbar v-model="snackbar.visible">
			<span>{{ snackbar.text }}</span>
			<template>
				<v-btn color="pink" text @click="snackbar.visible = false">Close</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@api/index";
import endpoints from "@api/repository";
import ConfirmationDialog from "@components/widgets/dialogs/ConfirmationDialog";
import Helper from "@utils/misc";
import TourHelper from "@utils/tour_helper";


export default {
	name: "AllPayrollGroups",

	data() {
		return {
			Helper: Helper,
			payrollGroups: [],
			options: {},
			pagination: {
				totalCount: 0,
				url: endpoints.getPayrollAccounts,
			},
			payrollDetailsForm: false,
			accounts: [],
			dialog: false,
			search: "",
			desiredGroup: {},

			// To avert api call when selected value is same as the old one
			apicheckcode: [],
			rules: {
				required: (v) => !!v || "Required",
			},
			selectedGroupDetails: {
				particulars: []
			},
			headers: [
				{
					text: "Employee ID",
					align: "start",
					value: "staff.employee_id",
				},
				{ text: "Photo", value: "staff.image", sortable: false },
				{ text: "Name", value: "staff.full_name", align: "center" },
				{ text: "Phone Number", value: "staff.phone", sortable: false },
				{
					text: "Group",
					value: "payroll_group.code",
					sortable: false,
					align: "center",
				},
			],
			snackbar: {
				visible: false,
				text: "",
			},
		};
	},
	components: {
		ConfirmationDialog,
	},
	computed: {
		...mapGetters(["currentUser", "accessToken", "isLoading"]),
		essentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
	},

	methods: {
		...mapActions(["setLoading"]),

		handleTour() {
        var steps = [];
        steps.push({id: '1', title: "Welcome to Payroll Groups", description: "Check your payroll groups from groups tab and allocate those groups from allocate tab,also click on group's card for editing payroll groups", videoLink: "https://youtube.com"});
        steps.push({id: '2', description: "Add your payroll group by filling necessary details.", position:'top' ,el: '#add-payroll-groups'});
        // steps.push({id: '4', description: "Font Image can be uploaded from here of aspect ratio 6:1:", el: '#upload-image-payslip'});
        // steps.push({id: '5', description: "Enable or disable payslip options, which should be shown on payslip", el: '#payslip-options'});
        // steps.push({id: '6', title: "Deduction Payroll Particulars",description: "View and edit your Deduction payroll particulars <br><br> <b>Note:</b>Edited particular may be affected in other pages.", el: '#deduction-particular-table'});
        TourHelper.startTour(this, "payroll__groups", steps);
    },


		// handle api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			console.log(err);
			if (err.response) {
				this.snackbar.text = "Unable to process request";
				this.snackbar.color = "error";
				this.snackbar.visible = true;
			}
		},

		getDialogTotalEarnings(){
			let earning_particulars = this.selectedGroupDetails.particulars.filter((particular) => particular.particular_type == 'E')
			let earning_total = Helper.totalSum(earning_particulars, "value")
			return earning_total
		},

		getDialogTotalDeductions(){
			let deduction_particulars = this.selectedGroupDetails.particulars.filter((particular) => particular.particular_type == 'D')
			let deduction_total = Helper.totalSum(deduction_particulars, "value")
			return deduction_total
		},

		getDialogNetSalary(){
			return this.getDialogTotalEarnings() - this.getDialogTotalDeductions()
		},

		// validation to allow only numeric input
		onlyNumber($event) {
			let keyCode = $event.keyCode ? $event.keyCode : $event.which;
			if (keyCode < 48 || keyCode > 57) {
				$event.preventDefault();
			}
		},

		// fetch all staffs' account data
		async getAllPayrollAccounts() {
			var response;
			var url = this.pagination.url;
			if (this.search) {
				url = endpoints.getPayrollAccounts + "?q=" + this.search;
			}
			response = await api.handleApi({
				apiEssentials: this.essentials,
				url: url,
				responseVar: response,
			});
			if (response) {
				this.accounts = response.results;
				this.pagination.totalCount = response.count;
				this.pagination.nextUrl = response.next;
				this.apicheckcode = [];
				for(var i=0; i<this.accounts.length; i++){
					var temp = {};
					if(this.accounts[i].payroll_group)
						temp.code = this.accounts[i].payroll_group.code;
					else
						temp.code = "unset";
					temp.account_id = this.accounts[i].id;
					this.apicheckcode[i] = {};
					this.$set(this.apicheckcode, i, temp);
				}
			}
		},

		// get payroll groups via api call
		async getAllPayrollGroups() {
			var response;
			response = await api.handleApi({
				url: endpoints.payrollGroups,
				apiEssentials: this.essentials,
				responseVar: response,
			});
			if (response) {
				this.payrollGroups = response;
			}
		},

		// get details for selected payroll group
		async getPayrollGroupData(item) {
			var response = await api.call(
				this.essentials,
				endpoints.payrollGroups + "?id=" + item.payroll_group.id
			);
			this.selectedGroupDetails = response;
			// this.desiredGroup = response;
			this.desiredGroup['loss_of_pay'] = null;
			this.desiredGroup['ot_rate'] = null;
			this.desiredGroup.particular_amounts = [];
			for (var i = 0; i < this.selectedGroupDetails.particulars.length; i++) {
				var temp = { particular: {} };
				temp.title = this.selectedGroupDetails.particulars[i].title;
				temp.particular.id = this.selectedGroupDetails.particulars[i].id;
				temp.amount = null;
				this.$set(this.desiredGroup.particular_amounts, i, temp);
			}
		},

		// To change payroll group
		savePayrollGroup(item) {
			for(var i=0; i<this.apicheckcode.length; i++){
				if(this.apicheckcode[i].account_id == item.id){
					if(this.apicheckcode[i].code == 'unset')
					{	
						this.apicheckcode[i].code = item.payroll_group.code;
						break;
					}
					else if(this.apicheckcode[i].code == item.payroll_group.code){
						return;
					}
					else{
						this.apicheckcode[i].code = item.payroll_group.code;
						break;
					}
				}
			}
			
			this.desiredGroup.staff_id = item.staff.id;
			this.desiredGroup.group_id = item.payroll_group.id;
			this.desiredGroup.id = item.id;
			this.getPayrollGroupData(item); // fetch particulars for the dialog text fields
			this.dialog = true;
		},

		// validate text fields of the dialog
		validate() {
			for (var i = 0; i < this.desiredGroup.particular_amounts.length; i++) {
				if (!this.desiredGroup.particular_amounts[i].amount) {
					// to check if any particular's amount field has been left blank
					this.snackbar.color = "error";
					this.snackbar.text = "Particular amounts cannot be left blank!";
					this.snackbar.visible = true;
					return false;
				}
			}

			return true;
		},

		// Changes the staff category via api request
		async groupChanged() {
			for (var i = 0; i < this.selectedGroupDetails.particulars.length; i++) {
				this.desiredGroup.particular_amounts[i].amount = this.selectedGroupDetails.particulars[i].value;
			}
			if (this.validate()) {
				// api call only if all fields validated
				var response = await api.call(
					this.essentials,
					endpoints.changePayrollGroup,
					api.Methods.POST,
					this.desiredGroup
				);

				if (response) {
					this.snackbar.text = "Payroll group changed successfully";
					this.snackbar.visible = true;
				}
				this.desiredGroup = {};
				this.dialog = false;
			}
		},

		// To close dialog
		async closeDialog() {
			var response;
			var url = endpoints.getPayrollAccounts + "?id=" + this.desiredGroup.id;
			response = await api.handleApi({
				apiEssentials: this.essentials,
				url: url,
				responseVar: response,
			});
			if (response) {
				// update the current item's group for which it was allocated/changed
				// this.accounts.find((x) => x.id == this.desiredGroup.id).payroll_group = response.payroll_group;

				for(var i=0; i<this.accounts.length; i++){
					if(this.accounts[i].id == this.desiredGroup.id){
						this.accounts[i].payroll_group = response.payroll_group;
						if(response.payroll_group.code!='')
						this.apicheckcode[i].code = response.payroll_group.code;
						else
						this.apicheckcode[i].code = 'unset';
					}
				}
			}
			this.desiredGroup = {};
			this.dialog = false;
		},

		redirectToDetails(id) {
			this.$router.push({
				name: "PayrollAccountDetails",
				params: { account_id: id },
			});
		}
	},
	watch: {
		options: {
			async handler() {
				this.pagination.url =
					endpoints.getPayrollAccounts + "?page=" + this.options.page;
				if (this.options.sortBy.length > 0) {
					var params = "&o=";
					for (var sortOption in this.options.sortBy) {
						params +=
							(this.options.sortDesc[sortOption] ? "" : "-") +
							this.options.sortBy[sortOption];
						if (sortOption != this.options.sortBy.length - 1) params += ",";
					}
					params = Helper.replaceTextFromArray(params, [
						["staff.full_name", "staff_hr_account__staff__full_name"],
						["staff.phone", "staff_hr_account__staff__phone"],
						[
							"staff.employee_id",
							"staff_hr_account__staff__staff_profile__employee_id",
						],
					]);

					this.pagination.url += params;
				}
				this.getAllPayrollAccounts();
			},
		},
		search: {
			async handler() {
				var searched = this.search;
				var self = this;
				setTimeout(function() {
					if (searched == self.search) {
						self.getAllPayrollAccounts();
					}
				}, 500);
			},
		},
	},
	created() {
		this.getAllPayrollGroups();
		this.getAllPayrollAccounts(); //new
		this.handleTour();
	},
};
</script>

<style scoped>
.group-card {
	word-wrap: break-word;
}
h2 {
	font-size: 24px;
}
.selections {
	max-width: min-content;
}
.v-input__slot.transparent {
	padding: 0 !important;
}
.rounded-input {
	border-radius: 12px !important;
}
</style>
